import { cn } from "@marketing-sites/utils/cn";
import type { CrossSiteCard } from "../../types";
import { HeaderLink } from "../header-link";
import { navFg, navFgMuted, navPanelBorder } from "../floating-nav-classes";

const accentBg: Record<CrossSiteCard["accentColor"], string> = {
  fin: "bg-fin",
  helpdesk: "bg-helpdesk",
};

const DotRow = ({
  accent,
  edge,
}: {
  accent: string;
  edge: "top" | "bottom";
}) => (
  <div
    aria-hidden
    className={cn(
      "absolute inset-x-1 flex justify-between",
      edge === "top" ? "top-1" : "bottom-1",
    )}
  >
    <span className={cn(accent, "size-1")} />
    <span className={cn(accent, "size-1")} />
    <span className={cn(accent, "size-1")} />
  </div>
);

export function SwitchHoverCard({
  card,
  analyticsId,
}: {
  card: CrossSiteCard;
  analyticsId: string;
}) {
  const accent = accentBg[card.accentColor];
  const { ctaLabel, ctaHref } = card;
  const hasCta = !!ctaLabel && !!ctaHref;
  const hasTitle = Boolean(card.titleLead || card.titleAccent);
  return (
    <div
      className={cn(
        "shared-header-switch-card",
        navPanelBorder,
        "invisible absolute right-0 top-full mt-[18px] w-[344px] -translate-y-1.5 border bg-[color-mix(in_oklab,var(--nav-panel-bg,#fff)_95%,transparent)] opacity-0",
        // Transition lives on the shown state, so the card animates in on hover
        // but snaps out instantly — matching the dropdown's instant close. The
        // backdrop-filter is likewise only applied while shown, so a permanently
        // mounted blur layer can't flash stale content on nearby repaints.
        "group-hover/switch:visible group-hover/switch:translate-y-0 group-hover/switch:opacity-100 group-hover/switch:backdrop-blur-[4px] group-hover/switch:transition-all group-hover/switch:duration-200 group-hover/switch:ease-out",
        "group-focus-within/switch:visible group-focus-within/switch:translate-y-0 group-focus-within/switch:opacity-100 group-focus-within/switch:backdrop-blur-[4px] group-focus-within/switch:transition-all group-focus-within/switch:duration-200 group-focus-within/switch:ease-out",
        hasCta ? "h-[256px]" : hasTitle ? "h-[192px]" : "h-[112px]",
      )}
    >
      <DotRow accent={accent} edge="top" />
      <DotRow accent={accent} edge="bottom" />
      {hasTitle && (
        <p
          className={cn(
            navFg,
            "absolute left-4 w-[300px] text-[24px] leading-none tracking-[-0.48px]",
            hasCta ? "top-[47px]" : "top-[31px]",
          )}
        >
          {card.titleLead}{" "}
          <span className={navFgMuted}>{card.titleAccent}</span>
        </p>
      )}
      <p
        className={cn(
          navFg,
          "absolute left-[15px] w-[320px] text-[12px] leading-[1.2]",
          !hasTitle
            ? "top-[31px] text-[15px]"
            : hasCta
              ? "top-[119px]"
              : "top-[103px]",
        )}
      >
        {card.description}
      </p>
      {hasCta && (
        <HeaderLink
          analyticsId={analyticsId}
          href={ctaHref}
          text={ctaLabel}
          external
          className={cn(
            navFg,
            "absolute left-[15px] top-[199px] flex h-10 items-center justify-center gap-1 rounded-[4px] border border-[var(--nav-fg,var(--color-off-black))] px-3 text-[14px] leading-[1.1] transition-colors duration-[400ms] ease-out hover:bg-[var(--nav-fg,var(--color-off-black))] hover:text-[var(--nav-panel-bg,#fff)]",
          )}
        >
          {card.ctaLabel}
          <span aria-hidden>→</span>
        </HeaderLink>
      )}
    </div>
  );
}
